/* eslint-disable @typescript-eslint/no-explicit-any, prefer-rest-params, prefer-spread -- snippets oficiales de cada plataforma: la cola necesita `arguments` tal cual */
/**
 * Snippets oficiales de cada plataforma, portados a funciones. Ninguno se ejecuta en el SSR ni
 * antes de hidratar: los llama `tracker.ts` cuando hay id configurado Y consentimiento de la
 * categoría. Todos los `<script>` van `async`, así que nunca bloquean el render.
 *
 * Cada loader deja la función global de la plataforma (`gtag`, `fbq`, `ttq`, `twq`, `lintrk`,
 * `clarity`) como una cola que el script real consume cuando termina de bajar: lo que se trackea
 * antes de que llegue no se pierde.
 */

import type { ClarityIds } from "./clarity-ids";

type AnyFn = (...args: any[]) => void;

export type VendorWindow = Window & {
  dataLayer?: unknown[];
  gtag?: AnyFn;
  fbq?: AnyFn & Record<string, any>;
  _fbq?: unknown;
  ttq?: any;
  TiktokAnalyticsObject?: string;
  twq?: AnyFn & Record<string, any>;
  lintrk?: AnyFn & { q?: unknown[] };
  _linkedin_partner_id?: string;
  _linkedin_data_partner_ids?: string[];
  clarity?: AnyFn & { q?: unknown[] };
};

const w = () => window as VendorWindow;

export function injectScript(src: string, id: string) {
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

type GoogleConsent = { analytics: boolean; ads: boolean };

const consentSignals = ({ analytics, ads }: GoogleConsent) => ({
  analytics_storage: analytics ? "granted" : "denied",
  ad_storage: ads ? "granted" : "denied",
  ad_user_data: ads ? "granted" : "denied",
  ad_personalization: ads ? "granted" : "denied",
});

/**
 * gtag.js (GA4 + Google Ads, un solo script). Consent Mode v2:
 * - `decided` (la persona eligió, o el aviso de cookies está apagado y se mide por defecto): el
 *   `default` ya sale con lo que rige, sin `wait_for_update`, para que la primera página vista
 *   no espere a ningún banner.
 * - sin decidir: las cuatro señales arrancan en `denied` (con `wait_for_update`) y recién después
 *   se actualizan con lo que la persona acepte.
 * GA4 va con `send_page_view: false` porque la app es SPA y la página vista la manda `tracker.ts`
 * en cada navegación.
 */
export function loadGoogle(opts: {
  ga4Id: string | null;
  adsId: string | null;
  consent: GoogleConsent;
  decided?: boolean;
}) {
  const win = w();
  if (!win.gtag) {
    win.dataLayer = win.dataLayer || [];
    // gtag necesita `arguments` (no un array): así lo espera el dataLayer.
    win.gtag = function gtag() {
      win.dataLayer!.push(arguments);
    };
    if (opts.decided) {
      win.gtag("consent", "default", consentSignals(opts.consent));
    } else {
      win.gtag("consent", "default", {
        ...consentSignals({ analytics: false, ads: false }),
        wait_for_update: 500,
      });
    }
    win.gtag("js", new Date());
  }
  win.gtag("consent", "update", consentSignals(opts.consent));

  const firstId = (opts.consent.analytics && opts.ga4Id) || (opts.consent.ads && opts.adsId);
  if (!firstId) return;
  if (opts.consent.analytics && opts.ga4Id) {
    win.gtag("config", opts.ga4Id, { send_page_view: false });
  }
  if (opts.consent.ads && opts.adsId) win.gtag("config", opts.adsId);
  injectScript(
    `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(firstId)}`,
    "academia-gtag",
  );
}

export function updateGoogleConsent(consent: GoogleConsent) {
  w().gtag?.("consent", "update", consentSignals(consent));
}

/** Meta Pixel. `disablePushState`: las páginas vistas las manda el tracker, no el píxel solo. */
export function loadMeta(pixelId: string, externalIdHash?: string) {
  const win = w();
  if (!win.fbq) {
    const n: any = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    win.fbq = n;
    if (!win._fbq) win._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    n.disablePushState = true;
    injectScript("https://connect.facebook.net/en_US/fbevents.js", "academia-meta-pixel");
  }
  win.fbq!("consent", "grant");
  win.fbq!("init", pixelId, externalIdHash ? { external_id: externalIdHash } : undefined);
}

const TIKTOK_METHODS = [
  "page",
  "track",
  "identify",
  "instances",
  "debug",
  "on",
  "off",
  "once",
  "ready",
  "alias",
  "group",
  "enableCookie",
  "disableCookie",
  "holdConsent",
  "revokeConsent",
  "grantConsent",
];

/** TikTok Pixel. */
export function loadTikTok(pixelId: string) {
  const win = w();
  if (!win.ttq) {
    const ttq: any = [];
    win.TiktokAnalyticsObject = "ttq";
    win.ttq = ttq;
    ttq.methods = TIKTOK_METHODS;
    ttq.setAndDefer = (target: any, method: string) => {
      target[method] = function () {
        target.push([method].concat(Array.prototype.slice.call(arguments, 0)));
      };
    };
    for (const method of TIKTOK_METHODS) ttq.setAndDefer(ttq, method);
    ttq.instance = (id: string) => {
      const instance = ttq._i[id] || [];
      for (const method of TIKTOK_METHODS) ttq.setAndDefer(instance, method);
      return instance;
    };
    ttq.load = (id: string, options?: Record<string, unknown>) => {
      const url = "https://analytics.tiktok.com/i18n/pixel/events.js";
      ttq._i = ttq._i || {};
      ttq._i[id] = [];
      ttq._i[id]._u = url;
      ttq._t = ttq._t || {};
      ttq._t[id] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[id] = options || {};
      injectScript(`${url}?sdkid=${encodeURIComponent(id)}&lib=ttq`, "academia-tiktok-pixel");
    };
  }
  win.ttq.load(pixelId);
  win.ttq.grantConsent();
}

/** X (Twitter) Pixel. `config` ya manda la página vista inicial. */
export function loadX(pixelId: string) {
  const win = w();
  if (!win.twq) {
    const s: any = function () {
      s.exe ? s.exe.apply(s, arguments) : s.queue.push(arguments);
    };
    s.version = "1.1";
    s.queue = [];
    win.twq = s;
    injectScript("https://static.ads-twitter.com/uwt.js", "academia-x-pixel");
  }
  win.twq!("config", pixelId);
}

/** LinkedIn Insight Tag. */
export function loadLinkedIn(partnerId: string) {
  const win = w();
  win._linkedin_partner_id = partnerId;
  win._linkedin_data_partner_ids = win._linkedin_data_partner_ids || [];
  if (!win._linkedin_data_partner_ids.includes(partnerId)) {
    win._linkedin_data_partner_ids.push(partnerId);
  }
  if (!win.lintrk) {
    const lintrk: any = (a: unknown, b: unknown) => lintrk.q.push([a, b]);
    lintrk.q = [];
    win.lintrk = lintrk;
  }
  injectScript("https://snap.licdn.com/li.lms-analytics/insight.min.js", "academia-linkedin");
}

/**
 * Microsoft Clarity, con su Consent API v2.
 *
 * `onPage` recibe los ids de la sesión que se está grabando, en CADA página: se registra una sola
 * vez `clarity("metadata", cb, wait=false, recall=true)`, el mismo patrón que usa el tag oficial.
 * `recall` hace que Clarity lo vuelva a llamar en cada navegación de la SPA (para Clarity cada
 * cambio de URL es una página nueva: para y vuelve a arrancar, y borra identify y tags). Queda en
 * la cola del stub y se ejecuta después del `start` del tag.
 */
export function loadClarity(
  projectId: string,
  consent: GoogleConsent,
  onPage?: (meta: ClarityIds) => void,
) {
  const win = w();
  if (!win.clarity) {
    const clarity: any = function () {
      (clarity.q = clarity.q || []).push(arguments);
    };
    win.clarity = clarity;
    injectScript(`https://www.clarity.ms/tag/${encodeURIComponent(projectId)}`, "academia-clarity");
    if (onPage) {
      clarity(
        "metadata",
        (meta: ClarityIds) => {
          try {
            onPage(meta);
          } catch {
            // Clarity no atrapa los errores del callback: uno nuestro no puede romper la grabación.
          }
        },
        false,
        true,
      );
    }
  } else {
    // Volvió a aceptar analítica después de revocarla: Clarity quedó parado (`stop`), y con
    // Clarity parado `consentv2` no hace nada. Primero se lo arranca. Si ya estaba andando,
    // `start` no hace nada (Clarity no arranca dos veces).
    win.clarity("start");
  }
  updateClarityConsent(consent);
}

export function updateClarityConsent({ analytics, ads }: GoogleConsent) {
  w().clarity?.("consentv2", {
    ad_Storage: ads ? "granted" : "denied",
    analytics_Storage: analytics ? "granted" : "denied",
  });
}

/**
 * Cookies first-party que dejan las plataformas. Al retirar el consentimiento se borran, en el
 * host y en el dominio padre (GA y Meta las ponen en `.cursosacademia.ar`).
 */
const TRACKING_COOKIE_PATTERNS = [
  /^_ga(_.*)?$/,
  /^_gid$/,
  /^_gcl_/,
  /^_fbp$/,
  /^_fbc$/,
  /^_ttp$/,
  /^_tt_enable_cookie$/,
  /^_clck$/,
  /^_clsk$/,
  /^li_fat_id$/,
  /^_uetsid$/,
  /^_uetvid$/,
  /^ph_/,
  /^personalization_id$/,
];

export function clearTrackingCookies() {
  const names = document.cookie
    .split(";")
    .map((part) => part.split("=")[0]?.trim())
    .filter((name): name is string => Boolean(name))
    .filter((name) => TRACKING_COOKIE_PATTERNS.some((pattern) => pattern.test(name)));
  const host = window.location.hostname;
  const parts = host.split(".");
  const domains = [""];
  for (let i = 0; i < parts.length - 1; i++) domains.push(`; domain=.${parts.slice(i).join(".")}`);
  for (const name of names) {
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; path=/${domain}`;
    }
  }
}
